import { Fragment, useEffect, useState } from 'react';
import { formatTimestamp } from 'sharedUi/dateUtils';
import type { LogTransaction, LogTemplate } from '../../types/log-transaction.types';
import '../error-analytics/error-analytics.css';

/**
 * Raw user identifiers are never surfaced in the UI, so payload keys that hold
 * one (userId, actorUserId, createdBy, …) are dropped before rendering.
 */
const USER_ID_KEY = /(^|_)(actor)?user_?id$|^actor_?id$|^(created|updated|deleted)_?by$/i;

const stripUserIds = (obj: unknown): Record<string, any> | null => {
  if (!obj || typeof obj !== 'object') return null;
  const kept = Object.entries(obj as Record<string, any>).filter(([k]) => !USER_ID_KEY.test(k));
  return kept.length ? Object.fromEntries(kept) : null;
};

const LEVEL_STATUS: Record<string, string> = { error: 'open', warning: 'investigating', info: 'resolved' };

type DrawerTab = 'overview' | 'parameters' | 'raw';

/** A log value as a line of text.
 *
 * Most values are scalars. The ones that are not — `keyFields` is an object on
 * most rows, `{userName, entityLabel}` — used to come out as raw JSON with its
 * braces and quotes, which is the shape of the data rather than what it says. A
 * flat object of scalars is read out as `key: value · key: value`; anything
 * deeper falls back to JSON, where the structure IS the information. */
const asText = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v !== 'object') return String(v);
  if (Array.isArray(v)) return v.map(asText).join(', ');
  const entries = Object.entries(v as Record<string, unknown>);
  const flat = entries.every(([, x]) => x === null || typeof x !== 'object');
  return flat
    ? entries.map(([k, x]) => `${k}: ${x ?? '—'}`).join(' · ')
    : JSON.stringify(v);
};

/* Timestamps are stored as IST wall-clock with no offset, so they must go through
   the shared formatter — a bare `new Date(...).toLocaleString()` shifts them. */
const fmtTime = (ts?: string) => {
  if (!ts) return '—';
  const out = formatTimestamp(ts);
  return out === '-' ? '—' : out;
};

interface LogDetailDrawerProps {
  open: boolean;
  log: LogTransaction | null;
  templates?: LogTemplate[];
  onClose: () => void;
}

const LogDetailDrawer = ({ open, log, templates, onClose }: LogDetailDrawerProps) => {
  const [tab, setTab] = useState<DrawerTab>('overview');

  // A fresh record always opens on Overview, and Esc closes the drawer
  useEffect(() => { setTab('overview'); }, [log?._id]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const template = log ? templates?.find((t) => t.logCode === log.logCode) || null : null;

  /** key field / sensitive markers come from the log_master template, not the row */
  const paramInfo = (key: string) => {
    const p = template?.parameters?.find((param: any) => param && typeof param === 'object' && param.name === key);
    return { isKeyField: !!p?.isKeyField, isSensitive: !!p?.isSensitive };
  };

  const parameters = stripUserIds(log?.parameters);
  const details = stripUserIds(log?.Details);
  const level = (log?.level || '').toLowerCase();

  /* Typed as unknown, NOT [string, string]: `keyFields` is an object on most
     rows — `{userName, entityLabel}` is what the log writer puts there — and the
     old cast only told the compiler otherwise. React was then handed an object as
     a child and the whole drawer fell to the error boundary ("Objects are not
     valid as a React child"). Every value goes through asText, as the parameter
     and detail grids already did. */
  const entityRows: [string, unknown][] = log
    ? ([
        ['Entity type', log.entityType],
        ['Entity ID', log.entityId],
        ['Org unit', log.orgUnitId],
        ['Request ID', log.requestId],
        ['Key fields', log.keyFields],
      ].filter(([, v]) => !!v) as [string, unknown][])
    : [];

  return (
    <div className="ea-root ea-portal">
      <div className={`ea-scrim ${open ? 'show' : ''}`} onClick={onClose} />
      <aside className={`ea-drawer ${open ? 'show' : ''}`} aria-label="Log detail">
        {log && (
          <>
            <div className="ea-dhead">
              <div className="ea-crumb" style={{ marginBottom: 12 }}>
                Audit Logs<span className="sep" style={{ margin: '0 7px' }}>›</span>
                Log Stream<span className="sep" style={{ margin: '0 7px' }}>›</span>
                <b>{log.logCode || 'Log'}</b>
              </div>
              <div className="top">
                <div>
                  <div className="ea-dcode">{log.logCode || '—'}</div>
                  <div className="ea-dttl">{log.message || 'No message recorded for this event.'}</div>
                </div>
                <button className="ea-iconbtn" onClick={onClose} aria-label="Close">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                {level && <span className={`ea-status ${LEVEL_STATUS[level] || 'resolved'}`}>{level.toUpperCase()}</span>}
                {log.layer && <span className="ea-status resolved" style={{ color: 'var(--ea-ink-3)', background: 'var(--ea-raised)' }}>{log.layer.toUpperCase()}</span>}
                {log.category && <span className="ea-sub" style={{ marginLeft: 'auto' }}>{log.category}</span>}
              </div>
              <div className="ea-dstats">
                {([
                  ['Occurred at', fmtTime(log.EventTimeStamp || log.createdAt), 'When this specific event was recorded'],
                  ['Actor', log.actorName || (log.actorId ? 'Unknown user' : '—'), 'Who performed the action'],
                  ['Retention', log.retentionPeriod ? `${log.retentionPeriod}d` : '—', 'How long this log is kept, per its template'],
                  ['Source IP', log.createdIp || '—', 'Where the request came from'],
                ] as [string, string, string][]).map(([l, v, t]) => (
                  <div className="ea-dstat" key={l} title={t}>
                    <div className="l">{l}</div>
                    <div className="v" style={{ fontSize: l === 'Occurred at' || l === 'Actor' ? 12.5 : undefined }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ea-dbody">
              <div className="ea-tabs">
                {(['overview', 'parameters', 'raw'] as DrawerTab[]).map((t) => (
                  <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                    {t === 'raw' ? 'Raw JSON' : t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {tab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <div className="ea-dsec">Summary</div>
                    <div className="ea-kv">
                      <span className="k">Level</span><span className="v">{level ? level[0].toUpperCase() + level.slice(1) : '—'}</span>
                      <span className="k">Layer</span><span className="v">{log.layer?.toUpperCase() || '—'}</span>
                      <span className="k">Category</span><span className="v">{log.category || log.logtype || '—'}</span>
                      <span className="k">Actor</span><span className="v">{log.actorName || (log.actorId ? 'Unknown user' : 'Not attributed to a user')}</span>
                      {log.endpoint && <><span className="k">Endpoint</span><span className="v mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{log.endpoint}</span></>}
                      {log.createdIp && <><span className="k">IP</span><span className="v mono">{log.createdIp}</span></>}
                      <span className="k">Occurred at</span><span className="v">{fmtTime(log.EventTimeStamp || log.createdAt)}</span>
                      <span className="k">Retention</span><span className="v">{log.retentionPeriod ? `${log.retentionPeriod} days` : '—'}</span>
                    </div>
                  </div>

                  <div>
                    <div className="ea-dsec">Message</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ea-ink-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {log.message || 'No message recorded for this event.'}
                    </div>
                  </div>

                  {entityRows.length > 0 && (
                    <div>
                      <div className="ea-dsec">Entity</div>
                      <div className="ea-kv">
                        {entityRows.map(([k, v]) => (
                          <Fragment key={k}>
                            <span className="k">{k}</span>
                            <span className="v mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{asText(v)}</span>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === 'parameters' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {/* key-field and sensitive markers come from the template definition */}
                  <div>
                    <div className="ea-dsec">Parameters{parameters ? ` · ${Object.keys(parameters).length}` : ''}</div>
                    {parameters ? (
                      <div className="ea-params">
                        {Object.entries(parameters).map(([k, v]) => {
                          const info = paramInfo(k);
                          return (
                            <Fragment key={k}>
                              <span className="pk" title={info.isSensitive ? 'Sensitive field' : info.isKeyField ? 'Key field' : undefined}>
                                {info.isKeyField && '🔑 '}{info.isSensitive && '🛡 '}{k}
                              </span>
                              <span className="pv">{asText(v)}</span>
                            </Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="ea-modal-empty">No parameters recorded for this event.</div>
                    )}
                  </div>

                  {details && (
                    <div>
                      <div className="ea-dsec">Details · {Object.keys(details).length}</div>
                      <div className="ea-params">
                        {Object.entries(details).map(([k, v]) => (
                          <Fragment key={k}>
                            <span className="pk">{k}</span>
                            <span className="pv">{asText(v)}</span>
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === 'raw' && (
                <div>
                  <div className="ea-dsec">Raw payload</div>
                  <pre className="ea-strace">
                    {JSON.stringify(
                      {
                        logCode: log.logCode,
                        level: log.level,
                        layer: log.layer,
                        category: log.category,
                        timestamp: log.EventTimeStamp || log.createdAt,
                        endpoint: log.endpoint,
                        createdIp: log.createdIp,
                        actor: log.actorName,
                        retentionPeriod: log.retentionPeriod,
                        message: log.message,
                        parameters,
                        Details: details,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default LogDetailDrawer;
